import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import { getInitials } from '../../../../../core/utils/text';
import type { CmdkPersonItem } from '../models/cmdk.model';
import { PersonColourDirective } from '../../../../../shared/people/person-colour';
import { PERSON_CALLS } from '../../../../../shared/people/person-calls';
import { CallPresenceDotComponent } from '../../../../calls/dock/call-presence-dot.component';

interface PersonFact {
  readonly label: string;
  readonly value: string;
}

/**
 * The preview for a colleague.
 *
 * AI CONTEXT — presence only from Flow Live, no avatar image, no "people nearby"
 *
 * The search returns identity plus, at most, today's desk booking. It carries
 * no photo URL and no presence state, so the mark here is initials, and no dot
 * is ever inferred from a booking: that would assert someone is in the office
 * when all we know is that a desk has their name on it. The dot that IS drawn
 * is Flow Live's presence (`im-call-presence-dot`, `presenceChanged`), and Call
 * rings them (PERSON_CALLS), both only with `flow.calls` on.
 *
 * Where the booking IS present it becomes "Sitting at", which is the claim the
 * data actually supports. Where it is absent the pane says so plainly rather
 * than leaving a gap the user has to interpret.
 */
@Component({
  selector: 'im-cmdk-preview-person',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperButtonComponent, PaperKbdComponent, PersonColourDirective, CallPresenceDotComponent],
  templateUrl: './cmdk-preview-person.component.html',
  styleUrl: './cmdk-preview-person.component.scss',
})
export class CmdkPreviewPersonComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkPersonItem>();
  readonly activated = output<void>();
  /** Call pressed: the palette closes and the call dock takes over. */
  readonly call = output<void>();

  private readonly calls = inject(PERSON_CALLS);
  protected readonly canCall = computed(() => this.calls.canCall(this.item().id));
  protected readonly inCall = computed(() => this.calls.presence(this.item().id)?.inCall === true);

  protected readonly initials = computed(() => getInitials(this.item().title));

  protected readonly facts = computed<PersonFact[]>(() => {
    this.locale.locale();
    const item = this.item();
    const t = (key: string): string => this.transloco.translate(`shell.cmdk.preview_person.${key}`);
    const out: PersonFact[] = [];
    if (item.jobTitle) out.push({ label: t('role'), value: item.jobTitle });
    if (item.department) out.push({ label: t('team'), value: item.department });
    if (item.email) out.push({ label: t('email'), value: item.email });
    out.push({
      label: t('sitting'),
      value: item.locationLabel ?? t('unknown_location'),
    });
    return out;
  });

  protected readonly detailsLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_person.details');
  });

  protected readonly callLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_person.call');
  });

  protected readonly inCallLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_person.in_call');
  });

  protected readonly openLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview_person.open');
  });
}
